"use client";

import React, { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import imageCompression from "browser-image-compression";

import { CardProps } from "../../store";
import * as S from "./styles";
import FileUploadSpinner from "../FileUploadSpinner";

const MAX_FILE_SIZE_MB = 70;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

// 전체 용량 제한 (200MB)
export const MAX_TOTAL_FILE_SIZE = 200 * 1024 * 1024;

// 이미지 압축 옵션
const IMAGE_MAX_SIZE_MB = 1.0; // 약 1MB
const IMAGE_MAX_WIDTH = 1280;

const InputFile = ({ id }: Pick<CardProps, "id">) => {
  const { control, setValue } = useFormContext();
  const [isCompressing, setIsCompressing] = useState(false);

  // 기존 파일
  const currentFiles = useWatch({
    control,
    name: id,
    defaultValue: [],
  }) as File[];

  /**
   * 개별 파일 용량 체크
   */
  const checkFileSize = (files: FileList | null) => {
    if (!files) return true;

    for (let i = 0; i < files.length; i += 1) {
      if (files[i].size > MAX_FILE_SIZE) {
        alert(`파일 크기는 ${MAX_FILE_SIZE_MB}MB를 초과할 수 없습니다.`);
        return false;
      }
    }
    return true;
  };

  /**
   * 이미지 압축
   */
  const compressFile = async (file: File): Promise<File> => {
    if (!file.type.startsWith("image/")) return file;

    // ✅ 1MB 이하 → 압축 안함
    if (file.size < IMAGE_MAX_SIZE_MB * 1024 * 1024) {
      return file;
    }

    try {
      const compressedBlob = await imageCompression(file, {
        maxSizeMB: IMAGE_MAX_SIZE_MB,
        maxWidthOrHeight: IMAGE_MAX_WIDTH,
        useWebWorker: true,
      });

      // 🔥 핵심: File로 다시 생성
      const compressedFile = new File(
        [compressedBlob],
        file.name, // 기존 파일명 유지
        {
          type: compressedBlob.type || file.type,
          lastModified: Date.now(),
        },
      );

      // console.log("🟢 압축 성공:", {
      //   original: file.size,
      //   compressed: compressedFile.size,
      //   name: compressedFile.name,
      //   type: compressedFile.type,
      //   isFile: compressedFile instanceof File,
      // });

      return compressedFile;
    } catch (e) {
      console.error("❌ 압축 실패:", e);
      return file;
    }
  };

  return (
    <Controller
      name={id}
      control={control}
      render={() => (
        <>
          {isCompressing && <FileUploadSpinner />}

          <S.FileInput
            type="file"
            multiple
            onChange={async (e) => {
              const newFiles = Array.from(e.target.files ?? []);
              if (!checkFileSize(e.target.files)) {
                e.target.value = "";
                return;
              }
              try {
                setIsCompressing(true);
                /**
                 * 1. 이미지 파일만 압축
                 */
                const processedFiles = await Promise.all(
                  newFiles.map(async (file) => {
                    // ✅ 이미지인지 체크
                    if (file.type.startsWith("image/")) {
                      return compressFile(file);
                    }
                    // ✅ 이미지 아니면 그대로 반환
                    return file;
                  }),
                );
                /**
                 * 2. 기존 + 신규 파일 합치기
                 */
                const mergedFiles = [...currentFiles, ...processedFiles];
                /**
                 * 3. 전체 용량 체크 (압축 후 기준)
                 */
                const totalSize = mergedFiles.reduce((sum, file) => sum + file.size, 0);
                if (totalSize > MAX_TOTAL_FILE_SIZE) {
                  const totalMB = (totalSize / 1024 / 1024).toFixed(1);
                  alert(
                    `전체 파일 용량은 ${
                      MAX_TOTAL_FILE_SIZE / 1024 / 1024
                    }MB를 초과할 수 없습니다.\n현재: ${totalMB}MB`,
                  );
                  e.target.value = "";
                  return;
                }
                /**
                 * 4. 상태 반영
                 */
                setValue(id, mergedFiles, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                // console.log("업데이트된 파일 목록:", mergedFiles);
              } finally {
                setIsCompressing(false);
                e.target.value = "";
              }
            }}
            id="file-upload"
          />
        </>
      )}
    />
  );
};

export default InputFile;
